import React, { useMemo, useState } from 'react';
import { Clock3, ImageOff, SearchX, Star } from 'lucide-react';
import {
  getRestaurantDeliveryLabel,
  isRestaurantOrderable,
  restaurantMatches
} from './restaurantUtils';
import { RestaurantEmptyState, RestaurantSkeletons } from './RestaurantFeedback';

function RestaurantImage({ restaurant }) {
  const source = restaurant.cover_url || restaurant.logo_url;
  if (!source) {
    return <div className="rc-image-fallback"><ImageOff aria-hidden="true" /><span>PROVI</span></div>;
  }
  return <img src={source} alt="" loading="lazy" />;
}

function RestaurantCard({ restaurant, onSelect }) {
  const orderable = isRestaurantOrderable(restaurant);
  return (
    <button type="button" className="rc-restaurant-card" onClick={() => onSelect(restaurant)}>
      <div className="rc-restaurant-card__image">
        <RestaurantImage restaurant={restaurant} />
        {restaurant.promo_text && <span className="rc-promo-badge">{restaurant.promo_text}</span>}
      </div>
      <div className="rc-restaurant-card__body">
        <div className="rc-restaurant-card__title">
          <h3>{restaurant.name}</h3>
          {Number(restaurant.rating) > 0 && (
            <span><Star aria-hidden="true" />{Number(restaurant.rating).toFixed(1)}</span>
          )}
        </div>
        {(restaurant.category || restaurant.description) && (
          <p>{restaurant.category || restaurant.description}</p>
        )}
        <div className="rc-restaurant-card__meta">
          {restaurant.estimated_time && <span><Clock3 aria-hidden="true" />{restaurant.estimated_time}</span>}
          <span className={`rc-status-dot ${orderable ? '' : 'is-unavailable'}`}>
            {getRestaurantDeliveryLabel(restaurant)}
          </span>
        </div>
      </div>
    </button>
  );
}

export default function RestaurantHome({
  restaurants,
  loading,
  error,
  query,
  onRetry,
  onSelect
}) {
  const [category, setCategory] = useState('all');
  const categories = useMemo(
    () => [...new Set(restaurants.map((restaurant) => restaurant.category).filter(Boolean))],
    [restaurants]
  );
  if (loading) return <RestaurantSkeletons />;
  if (error) {
    return (
      <RestaurantEmptyState
        error
        title="No pudimos cargar los restaurantes"
        message={error}
        actionLabel="Reintentar"
        onAction={onRetry}
      />
    );
  }
  if (!restaurants.length) {
    return (
      <RestaurantEmptyState
        title="Todavía no hay restaurantes disponibles"
        message="Los restaurantes activos aparecerán aquí cuando estén listos para recibir pedidos."
        actionLabel="Actualizar"
        onAction={onRetry}
      />
    );
  }

  const filtered = restaurants.filter((restaurant) =>
    restaurantMatches(restaurant, query)
    && (category === 'all' || restaurant.category === category)
  );
  if (!filtered.length) {
    return (
      <RestaurantEmptyState
        title="No encontramos coincidencias"
        message="Prueba con otro restaurante, categoría o plato."
      />
    );
  }

  const promoted = filtered.filter((restaurant) => restaurant.promo_text);
  return (
    <div className="rc-home">
      <section className="rc-home-intro">
        <div>
          <span className="rc-eyebrow">RESTAURANTES PROVI</span>
          <h1>Restaurantes cerca de ti</h1>
          <p>Locales activos y menús reales en PROVI.</p>
        </div>
      </section>

      {categories.length > 0 && (
        <nav className="rc-category-chips" aria-label="Categorías de restaurantes">
          <button type="button" className={category === 'all' ? 'is-active' : ''} onClick={() => setCategory('all')}>Todos</button>
          {categories.map((item) => (
            <button type="button" key={item} className={category === item ? 'is-active' : ''} onClick={() => setCategory(item)}>{item}</button>
          ))}
        </nav>
      )}

      {promoted.length > 0 && (
        <section className="rc-section">
          <div className="rc-section__heading">
            <div>
              <span className="rc-eyebrow">PROMOCIONES REALES</span>
              <h2>Descubre algo especial</h2>
            </div>
            <span>{promoted.length} disponibles</span>
          </div>
          <div className="rc-horizontal-cards">
            {promoted.map((restaurant) => (
              <RestaurantCard key={restaurant.id} restaurant={restaurant} onSelect={onSelect} />
            ))}
          </div>
        </section>
      )}

      <section className="rc-section">
        <div className="rc-section__heading">
          <div>
            <span className="rc-eyebrow">CERCA DE TI</span>
            <h2>Restaurantes disponibles</h2>
          </div>
          <span>{filtered.length} locales</span>
        </div>
        <div className="rc-card-grid">
          {filtered.map((restaurant) => (
            <RestaurantCard key={restaurant.id} restaurant={restaurant} onSelect={onSelect} />
          ))}
        </div>
      </section>
      {query && (
        <p className="rc-search-summary"><SearchX aria-hidden="true" />Resultados filtrados por “{query}”</p>
      )}
    </div>
  );
}
